// HST — Frise chronologique du dépôt
// <FriseChronologique depot={depot} tree={DEV_TREE} onPick={(id) => …} />
// depot.history : { P2: 2, P3: 4, … } = semaine de construction de chaque étape (facultatif)
// depot.week    : semaine de jeu en cours
// 21/10/2026 — libellés bilingues (clés d3_* du système i18n du jeu).

import React, { useState } from 'react';
import Icon from './Icons';
import { DEV_TREE, nodeState, contractsNeeding, recommendNext } from './hstModel';
import { profileOf } from './depotProfiles';
import { useTranslation } from '../../i18n';
import './ArbreFrise.css';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n);

export default function FriseChronologique({ depot, tree = DEV_TREE, onPick, defaultOpen = true }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const built = new Set(depot.built || []);
  const prof = profileOf(depot);
  const acq = prof.acquired;
  const hist = depot.history || {};
  const done = (depot.built || [])
    .map((id, i) => ({ node: tree.find((n) => n.id === id), week: hist[id], i }))
    .filter((x) => x.node && !acq.has(x.node.id))
    .sort((a, b) => (a.week ?? 999 + a.i) - (b.week ?? 999 + b.i));
  const reco = recommendNext(depot, tree);
  const next = tree.filter((n) => nodeState(n.id, built, tree, depot) === 'available')
    .sort((a, b) => (reco && b.id === reco.node.id) - (reco && a.id === reco.node.id) || a.cost - b.cost);
  const locked = tree.length - done.length - next.length;
  const invested = done.reduce((s, d) => s + d.node.cost, 0);
  const chargesAll = (depot.built || []).map((id) => tree.find((n) => n.id === id)).filter(Boolean).reduce((s, n) => s + (n.monthly || 0), 0);
  let cumulStart = (depot.built || []).filter((id) => acq.has(id)).map((id) => tree.find((n) => n.id === id)).filter(Boolean).reduce((s, n) => s + (n.monthly || 0), 0);
  let cumul = cumulStart;
  // unité de temps : semaines (S12) ou jours de jeu (J84) selon le jeu
  const isDay = depot.timeUnit === 'jour';
  const U = isDay ? { p: t('d3_unit_day_short'), label: t('d3_unit_day') } : { p: t('d3_unit_week_short'), label: t('d3_unit_week') };
  const plural = (n, key) => t(n > 1 ? `${key}_plural` : key, { n });

  return (
    <section className="hstx">
      <header className="hstx-head">
        <h2><Icon name="history" /> {t('d3_timeline_title')}</h2>
        <div className="hstx-chips">
          <span><b>{(depot.built || []).length}/{tree.length}</b> {t('d3_steps')}</span>
          <span>{t('d3_invested')} <b>{fmt(invested)} €</b></span>
          <span>{t('d3_charges')} <b className="is-red">{fmt(chargesAll)} €/m</b></span>
          {depot.week != null && <span>{U.label} <b>{depot.week}</b></span>}
        </div>
        <button className="hstx-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>{open ? t('d3_collapse') : t('d3_expand')}</button>
      </header>

      {open && (
        <div className="hstx-scroll">
          <ol className="hstx-frise">
            <li className="hstx-step is-built is-origin">
              <span className="hstx-when">{U.p}{hist.P1 ?? (isDay ? 0 : 1)}</span>
              <span className="hstx-dot"><Icon name={prof.rival ? 'building' : 'home'} size={14} /></span>
              <div className="hstx-card">
                <b>{prof.rival ? t('d3_takeover_of', { name: prof.rival.name }) : t('d3_site_opening')}</b>
                <small>{prof.rival ? t('d3_taken_over', { list: [t('d3_site'), ...acq].join(', ') }) : t('d3_yard_gate_hq')}</small>
                <span className="hstx-cumul">{t(prof.country.key)} · {t(prof.theme.key)}</span>
              </div>
            </li>
            {done.map(({ node, week }) => {
              cumul += node.monthly || 0;
              const serves = contractsNeeding(node.id, depot).length;
              return (
                <li key={node.id} className="hstx-step is-built">
                  <span className="hstx-when">{week != null ? `${U.p}${week}` : '—'}</span>
                  <span className="hstx-dot"><Icon name={node.icon} size={14} /></span>
                  <button className="hstx-card" onClick={() => onPick && onPick(node.id)}>
                    <em>{node.id}</em>
                    <b>{node.name}</b>
                    <small>{node.cost ? `${fmt(node.cost)} €` : t('d3_free')}{node.monthly ? ` · +${fmt(node.monthly)} €/m` : ''}</small>
                    <span className="hstx-cumul">{t('d3_total_charges', { amount: fmt(cumul) })}</span>
                    {serves > 0 && <span className="hstx-tag is-ok">{plural(serves, 'd3_serves_contracts')}</span>}
                  </button>
                </li>
              );
            })}
            <li className="hstx-now" aria-label={t('d3_today')}>
              <span className="hstx-when">{U.p}{depot.week ?? '—'}</span>
              <span className="hstx-nowline" />
              <span className="hstx-nowlabel">{t('d3_today')}</span>
            </li>
            {next.map((node) => {
              const waiting = contractsNeeding(node.id, depot).length;
              return (
                <li key={node.id} className="hstx-step is-next">
                  <span className="hstx-when">{t('d3_upcoming')}</span>
                  <span className="hstx-dot"><Icon name={node.icon} size={14} /></span>
                  <button className="hstx-card" onClick={() => onPick && onPick(node.id)}>
                    <em>{node.id}</em>
                    <b>{node.name}</b>
                    <small>{node.cost ? `${fmt(node.cost)} €` : t('d3_free')}{node.monthly ? ` · +${fmt(node.monthly)} €/m` : ''}</small>
                    {reco && reco.node.id === node.id ? <span className="hstx-tag is-gold">{t('d3_recommended')}</span> : waiting > 0 ? <span className="hstx-tag is-warn">{plural(waiting, 'd3_waiting_contracts')}</span> : <span className="hstx-tag is-gold">{t('d3_available')}</span>}
                  </button>
                </li>
              );
            })}
            {locked > 0 && (
              <li className="hstx-step is-locked">
                <span className="hstx-when">&nbsp;</span>
                <span className="hstx-dot"><Icon name="lock" size={13} /></span>
                <div className="hstx-card"><b>{plural(locked, 'd3_locked_steps')}</b><small>{t('d3_see_tree')}</small></div>
              </li>
            )}
          </ol>
        </div>
      )}
    </section>
  );
}
